<template>
  <v-container fluid>
    <v-card outlined class="pa-4">
      <v-form ref="taskForm" lazy-validation @submit.prevent="onSubmit">
        <div class="text-h6">Создание задания на обзвон</div>
        <v-divider class="my-3" />

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.name"
              label="Название *"
              filled
              dense
              :rules="[(v) => !!v || 'Обязательное поле']"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.project_id"
              :items="projectsList"
              :loading="projectsLoading"
              item-text="name"
              item-value="id"
              label="Телефония *"
              filled
              dense
              clearable
              :rules="[(v) => v != null && v !== '' || 'Выберите телефонию']"
              @change="onProjectChange"
            />
          </v-col>
          <v-col v-if="form.project_id" cols="12" md="3">
            <v-text-field
              v-model.number="form.sim_limit"
              label="Количество линий (SIM)"
              type="number"
              min="0"
              filled
              dense
            />
          </v-col>
          <v-col v-if="form.project_id" cols="12" md="3">
            <v-text-field
              v-model.number="form.cps_limit"
              label="CPS"
              type="number"
              min="0"
              filled
              dense
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="form.agent_uuid"
              :items="agentsList"
              :loading="agentsLoading"
              item-text="name"
              item-value="uuid"
              label="Агент *"
              filled
              dense
              clearable
              :rules="[(v) => !!v || 'Выберите агента']"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="dateRangeDisplay"
              label="Диапазон дат *"
              filled
              dense
              :rules="[(v) => isValidDateRange(v) || 'Корректный диапазон (ДД.ММ.ГГГГ — ДД.ММ.ГГГГ)']"
              placeholder="ДД.ММ.ГГГГ — ДД.ММ.ГГГГ"
              hint="Можно ввести вручную или выбрать в календаре"
              @input="onManualDateChange"
            >
              <template #append-outer>
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  min-width="290px"
                  offset-y
                >
                  <template #activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" aria-label="Календарь">
                      <v-icon>mdi-calendar</v-icon>
                    </v-btn>
                  </template>
                  <v-date-picker
                    v-model="dateRange"
                    range
                    @input="onDateRangeInput"
                  />
                </v-menu>
              </template>
            </v-text-field>
          </v-col>
        </v-row>

        <div class="my-4">
          <div v-if="uploadedFileId" class="mb-2">
            Прикреплённый файл:
            <v-chip
              class="ml-2"
              color="primary"
              outlined
              small
              close
              @click:close="removeFile"
            >
              {{ uploadedFileName }}
            </v-chip>
          </div>
          <v-btn
            color="success"
            small
            :loading="uploading"
            @click="openFilePicker"
          >
            <v-icon left small>mdi-attachment</v-icon>
            Прикрепить файл контактов
          </v-btn>
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-icon v-bind="attrs" small class="ml-1" v-on="on">mdi-help-circle-outline</v-icon>
            </template>
            <span>Файл .csv, .xlsx, .xls</span>
          </v-tooltip>
          <input
            ref="fileInput"
            type="file"
            accept=".csv,.xlsx,.xls"
            class="d-none"
            @change="onFileSelected"
          />
        </div>

        <div class="mt-4">
          <div class="subtitle-1 font-weight-medium">Расписание звонков *</div>
          <v-row>
            <v-col cols="12" md="4">
              <v-select
                v-model="schedule.weekday"
                :items="weekdayOptions"
                item-text="text"
                item-value="value"
                label="Дни недели"
                multiple
                chips
                deletable-chips
                outlined
                dense
                :rules="[(v) => (v && v.length) || 'Выберите хотя бы один день']"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="schedule.start_time"
                label="Время начала"
                type="time"
                outlined
                dense
                :rules="[(v) => !!v || 'Обязательно']"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="schedule.end_time"
                label="Время окончания"
                type="time"
                outlined
                dense
                :rules="[(v) => !!v || 'Обязательно']"
              />
            </v-col>
          </v-row>
        </div>

        <v-card flat outlined class="mt-4">
          <v-card-text>
            <div class="subtitle-1 font-weight-medium">Перезвоны</div>
            <v-text-field
              v-model="form.retry_intervals_str"
              class="mt-2"
              label="Перезвон через (минуты)"
              outlined
              dense
              hint="Например: 60"
            />

            <v-row class="mt-2">
              <v-col cols="12" sm="4">
                <v-checkbox
                  v-model="form.retry_on_reject"
                  label="Не ответили / Сбросили"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-checkbox
                  v-model="form.retry_on_autoresponder"
                  label="Автоответчик"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-checkbox
                  v-model="form.retry_on_short_call"
                  label="Короткий разговор"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-text-field
              v-if="form.retry_on_short_call"
              v-model="form.short_call_duration"
              class="mt-2"
              label="Макс. длительность короткого звонка (сек)"
              type="number"
              min="0"
              outlined
              dense
            />
          </v-card-text>
        </v-card>

        <v-row class="mt-2">
          <v-col cols="12" sm="4">
            <v-checkbox
              v-model="form.use_local_timezone"
              label="Использовать локальный часовой пояс"
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="4">
            <v-checkbox
              v-model="form.is_fz230_enabled"
              label="Включить ФЗ-230"
              hide-details
            />
          </v-col>
        </v-row>

        <v-divider class="my-4" />
        <div class="d-flex justify-end">
          <v-btn class="mr-2" @click="$router.back()">Отмена</v-btn>
          <v-btn color="primary" type="submit" :loading="saving">Создать</v-btn>
        </div>
      </v-form>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'CallTasksCreate',
  data() {
    const today = new Date()
    const todayStr = today.toISOString().slice(0, 10)
    const nextWeek = new Date(today)
    nextWeek.setDate(today.getDate() + 7)
    const nextWeekStr = nextWeek.toISOString().slice(0, 10)
    return {
      dateMenu: false,
      form: {
        name: '',
        project_id: null,
        sim_limit: null,
        cps_limit: null,
        agent_uuid: '',
        start_date: todayStr,
        end_date: nextWeekStr,
        retry_intervals_str: '',
        retry_on_reject: true,
        retry_on_autoresponder: true,
        retry_on_short_call: true,
        short_call_duration: 3,
        use_local_timezone: true,
        is_fz230_enabled: false,
      },
      schedule: {
        weekday: [],
        start_time: '09:00',
        end_time: '18:00',
      },
      dateRange: [todayStr, nextWeekStr],
      dateRangeDisplay: '',
      uploadedFileId: null,
      uploadedFileName: '',
      uploading: false,
      saving: false,
      projectsList: [],
      projectsLoading: false,
      agentsList: [],
      agentsLoading: false,
      weekdayOptions: [
        { text: 'ПН', value: 0 },
        { text: 'ВТ', value: 1 },
        { text: 'СР', value: 2 },
        { text: 'ЧТ', value: 3 },
        { text: 'ПТ', value: 4 },
        { text: 'СБ', value: 5 },
        { text: 'ВС', value: 6 },
      ],
    }
  },
  mounted() {
    this.loadAgents()
    this.loadProjects()
    this.updateDisplayFromDates()
  },
  methods: {
    ...mapActions('call_tasks', [
      'fetchProjects',
      'fetchAgents',
      'uploadBase',
      'createTask',
    ]),
    notifyError(message) {
      this.$store.commit('shared/SET_ERROR', { message }, { root: true })
    },
    notifySuccess(message) {
      this.$store.commit('shared/SET_SUCCESS', { message }, { root: true })
    },
    onProjectChange(projectId) {
      if (projectId == null || projectId === '') {
        this.form.sim_limit = null
        this.form.cps_limit = null
        return
      }
      const project = this.projectsList.find((p) => p.id === projectId)
      if (!project) return
      this.form.sim_limit = project.sim_limit ?? null
      this.form.cps_limit = project.cps_limit ?? null
    },
    async loadProjects() {
      this.projectsLoading = true
      try {
        const data = await this.fetchProjects()
        this.projectsList = Array.isArray(data) ? data : data.data || []
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
        this.notifyError('Ошибка загрузки списка телефонии')
      } finally {
        this.projectsLoading = false
      }
    },
    async loadAgents() {
      this.agentsLoading = true
      try {
        const data = await this.fetchAgents()
        this.agentsList = Array.isArray(data) ? data : data.data || []
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
        this.notifyError('Ошибка загрузки списка агентов')
      } finally {
        this.agentsLoading = false
      }
    },
    openFilePicker() {
      this.$refs.fileInput.click()
    },
    async onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      this.uploading = true
      try {
        const res = await this.uploadBase(file)
        this.uploadedFileId = res.file_id
        this.uploadedFileName = file.name
        this.notifySuccess('Файл загружен')
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
        this.notifyError('Ошибка загрузки файла')
      } finally {
        this.uploading = false
        event.target.value = ''
      }
    },
    removeFile() {
      this.uploadedFileId = null
      this.uploadedFileName = ''
    },
    formatDateForDisplay(dateStr) {
      if (!dateStr) return ''
      const [year, month, day] = dateStr.split('-')
      if (!day || !month || !year) return ''
      return `${day}.${month}.${year}`
    },
    parseDateFromDisplay(displayStr) {
      const s = String(displayStr).trim()
      const parts = s.split('.')
      if (parts.length !== 3) return null
      let [day, month, year] = parts
      day = String(day).trim().padStart(2, '0')
      month = String(month).trim().padStart(2, '0')
      year = String(year).trim()
      if (!year || !month || !day) return null
      return `${year}-${month}-${day}`
    },
    updateDisplayFromDates() {
      if (this.form.start_date && this.form.end_date) {
        const from = this.formatDateForDisplay(this.form.start_date)
        const to = this.formatDateForDisplay(this.form.end_date)
        if (from === to) {
          this.dateRangeDisplay = from
        } else {
          this.dateRangeDisplay = `${from} — ${to}`
        }
      } else {
        this.dateRangeDisplay = ''
      }
    },
    isValidDateRange(val) {
      if (!val) return false
      const parts = val.split(' — ')
      if (parts.length === 1) {
        return this.parseDateFromDisplay(parts[0]) !== null
      }
      if (parts.length === 2) {
        return (
          this.parseDateFromDisplay(parts[0]) != null &&
          this.parseDateFromDisplay(parts[1]) != null
        )
      }
      return false
    },
    onManualDateChange(newVal) {
      if (!newVal) {
        this.form.start_date = ''
        this.form.end_date = ''
        this.dateRange = []
        return
      }
      const parts = newVal.split(' — ')
      if (parts.length === 1) {
        const date = this.parseDateFromDisplay(parts[0].trim())
        if (date) {
          this.form.start_date = date
          this.form.end_date = date
          this.dateRange = [date, date]
        }
      } else if (parts.length === 2) {
        const from = this.parseDateFromDisplay(parts[0].trim())
        const to = this.parseDateFromDisplay(parts[1].trim())
        if (from && to) {
          this.form.start_date = from
          this.form.end_date = to
          this.dateRange = [from, to]
        }
      }
    },
    onDateRangeInput(val) {
      // В режиме range первый клик часто приходит как string — нельзя закрывать меню,
      // иначе не выбрать вторую дату. Закрываем только при готовом диапазоне [с, по].
      if (Array.isArray(val) && val.length === 2 && val[0] && val[1]) {
        this.form.start_date = val[0]
        this.form.end_date = val[1]
        this.dateRange = val
        this.updateDisplayFromDates()
        this.dateMenu = false
        return
      }
      if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) {
        this.form.start_date = ''
        this.form.end_date = ''
        this.dateRange = Array.isArray(val) ? val : []
        this.updateDisplayFromDates()
        return
      }
      this.dateRange = val
    },
    async onSubmit() {
      if (!this.$refs.taskForm.validate()) {
        this.notifyError('Заполните все обязательные поля')
        return
      }
      if (!this.uploadedFileId) {
        this.notifyError('Прикрепите файл с контактами')
        return
      }

      const payload = {
        name: this.form.name,
        project_id: this.form.project_id,
        lines_quantity: Number(this.form.sim_limit),
        cps_limit: Number(this.form.cps_limit),
        agent_uuid: this.form.agent_uuid,
        start_date: this.form.start_date,
        end_date: this.form.end_date,
        uploaded_file_id: this.uploadedFileId,
        schedules: [
          {
            weekday: this.schedule.weekday,
            start_time: this.schedule.start_time,
            end_time: this.schedule.end_time,
          },
        ],
        retry_on_reject: this.form.retry_on_reject,
        retry_on_autoresponder: this.form.retry_on_autoresponder,
        retry_on_short_call: this.form.retry_on_short_call,
        use_local_timezone: this.form.use_local_timezone,
        is_fz230_enabled: this.form.is_fz230_enabled,
      }

      if (this.form.retry_on_short_call && this.form.short_call_duration) {
        payload.short_call_duration = this.form.short_call_duration
      }
      if (this.form.retry_intervals_str.trim()) {
        payload.retry_intervals = this.form.retry_intervals_str
          .split(',')
          .map((v) => parseInt(String(v).trim(), 10))
          .filter((n) => !Number.isNaN(n))
      }

      this.saving = true
      try {
        await this.createTask(payload)
        this.notifySuccess('Задача успешно создана')
        this.$router.push({ name: 'call-tasks.list' })
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err)
        const message = (err.response && err.response.data && err.response.data.message) || 'Ошибка создания задачи'
        this.notifyError(message)
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
